import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import {
  CheckCircle as OkIcon,
  WarningAmber as WarnIcon,
  ErrorOutline as ErrorIcon,
  RadioButtonUnchecked as PendingIcon,
  PlayCircleOutline as InProgressIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

/**
 * Small chip that summarises a job's geofence-derived visit status.
 *
 * Props:
 *   status       - 'ok' | 'short' | 'over' | 'error' | null | undefined
 *   timeIn       - ISO timestamp of enter event (optional)
 *   timeOut      - ISO timestamp of exit event (optional; if missing with timeIn => on site now)
 *   allottedMinutes - the stop's allotted time; when set, an overtime visit
 *                  shows how far over it went (TKT-432)
 *   size         - MUI Chip size
 */
const GeofenceStatusChip = ({ status, timeIn, timeOut, allottedMinutes, size = 'small' }) => {
  const { t, i18n } = useTranslation();
  const locale = (i18n.language || 'en').startsWith('es') ? 'es-MX' : 'en-US';

  const fmt = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });
  };

  // Derive effective state.
  let variant; // key into config
  if (!status && !timeIn) variant = 'pending';
  else if (timeIn && !timeOut) variant = 'inProgress';
  else if (status === 'ok') variant = 'ok';
  else if (status === 'short') variant = 'short';
  else if (status === 'over') variant = 'over';
  else if (status === 'error') variant = 'error';
  else variant = 'pending';

  // Minutes on site (GPS arrival to departure) beyond the allotted time.
  let minutesOver = null;
  const allotted = Number(allottedMinutes);
  if (status === 'over' && timeIn && timeOut && allotted > 0) {
    const onSite = Math.round((new Date(timeOut) - new Date(timeIn)) / 60000);
    if (Number.isFinite(onSite) && onSite > allotted) minutesOver = onSite - allotted;
  }

  const config = {
    pending: {
      label: t('geofence.notVisited'),
      color: 'default',
      icon: <PendingIcon fontSize="small" />,
      tooltip: t('geofence.notVisitedTooltip'),
    },
    inProgress: {
      label: t('geofence.onSite'),
      color: 'info',
      icon: <InProgressIcon fontSize="small" />,
      tooltip: timeIn
        ? t('geofence.onSiteSince', { time: fmt(timeIn) })
        : t('geofence.onSite'),
    },
    ok: {
      label: t('geofence.completed'),
      color: 'success',
      icon: <OkIcon fontSize="small" />,
      tooltip: timeIn && timeOut
        ? t('geofence.completedAt', { timeIn: fmt(timeIn), timeOut: fmt(timeOut) })
        : t('geofence.completed'),
    },
    short: {
      label: t('geofence.short'),
      color: 'warning',
      icon: <WarnIcon fontSize="small" />,
      tooltip: t('geofence.shortTooltip'),
    },
    over: {
      label: minutesOver != null
        ? t('geofence.overtimeBy', { minutes: minutesOver })
        : t('geofence.overtime'),
      color: 'warning',
      icon: <WarnIcon fontSize="small" />,
      tooltip: minutesOver != null
        ? t('geofence.overtimeByTooltip', {
          onSite: minutesOver + allotted,
          allotted,
          timeIn: fmt(timeIn),
          timeOut: fmt(timeOut),
        })
        : timeIn
          ? t('geofence.overtimeSince', { time: fmt(timeIn) })
          : t('geofence.overtimeTooltip'),
    },
    error: {
      label: t('geofence.error'),
      color: 'error',
      icon: <ErrorIcon fontSize="small" />,
      tooltip: t('geofence.errorTooltip'),
    },
  };

  const cfg = config[variant];

  return (
    <Tooltip title={cfg.tooltip} arrow>
      <Chip
        icon={cfg.icon}
        label={cfg.label}
        color={cfg.color}
        size={size}
        aria-label={cfg.tooltip}
      />
    </Tooltip>
  );
};

export default GeofenceStatusChip;
